import { combineLatest, Observable, Subscription } from 'rxjs';
import { captureSlowWarning } from 'src/app/project/modules/errors/response-error';
import { TSlowTableLoadReport } from './utils/build-slow-load-report';

export const SLOW_TABLE_LOAD_TIMEOUT_MS = 60000;
export const SLOW_TABLE_LOAD_THRESHOLD_MS = 20000;

const SLOW_LOAD_MESSAGE = 'SLOW PAGE: Display table content';
const UNFINISHED_LOAD_MESSAGE = 'SLOW PAGE: Table did not finish loading within timeout';
const SLOW_LOAD_TAG = 'slow_table_load';

type TTableLoadOutcome = {
  elapsedMs: number;
  apiElapsedMs: number;
  completed: boolean;
};

type TTableLoadMonitorArgs = {
  apiLoading$: Observable<boolean>;
  tableLoading$: Observable<boolean>;
  buildReport: () => TSlowTableLoadReport;
};

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let loadingSubscription: Subscription | null = null;

export const clearTableLoadTimer = (): void => {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }

  if (loadingSubscription !== null) {
    loadingSubscription.unsubscribe();
    loadingSubscription = null;
  }
};

const reportSlowTableLoad = (report: TSlowTableLoadReport, outcome: TTableLoadOutcome): void => {
  const message = outcome.completed ? SLOW_LOAD_MESSAGE : UNFINISHED_LOAD_MESSAGE;
  const detail = outcome.completed
    ? `took ${outcome.elapsedMs}ms`
    : `did not finish within ${SLOW_TABLE_LOAD_TIMEOUT_MS}ms`;

  console.warn(`${message} - ${detail}`);

  captureSlowWarning({
    message,
    tag: SLOW_LOAD_TAG,
    view: report.view,
    workspaceId: report.workspaceId,
    extra: {
      url: report.url,
      userEmail: report.userEmail,
      userId: report.userId,
      accountId: report.accountId,
      keyword: report.keyword,
      grouping: report.grouping,
      searchPayload: report.searchPayload,
      elapsedMs: outcome.elapsedMs,
      apiElapsedMs: outcome.apiElapsedMs,
      completed: outcome.completed,
      timeoutMs: SLOW_TABLE_LOAD_TIMEOUT_MS,
    },
  });
};

export const startTableLoadTimer = ({
  apiLoading$,
  tableLoading$,
  buildReport,
}: TTableLoadMonitorArgs): void => {
  if (loadingSubscription !== null) {
    return;
  }

  const startedAt = Date.now();
  let apiStartedAt: number | null = null;
  let apiEndedAt: number | null = null;
  let hasStartedLoading = false;

  const apiElapsedMs = (): number =>
    apiStartedAt === null || apiEndedAt === null ? 0 : apiEndedAt - apiStartedAt;

  pendingTimer = setTimeout(() => {
    if (!hasStartedLoading) {
      clearTableLoadTimer();

      return;
    }

    reportSlowTableLoad(buildReport(), {
      elapsedMs: Date.now() - startedAt,
      apiElapsedMs: apiElapsedMs(),
      completed: false,
    });
    clearTableLoadTimer();
  }, SLOW_TABLE_LOAD_TIMEOUT_MS);

  loadingSubscription = combineLatest([apiLoading$, tableLoading$]).subscribe(
    ([apiLoading, tableLoading]) => {
      if (apiLoading && apiStartedAt === null) {
        apiStartedAt = Date.now();
      } else if (!apiLoading && apiStartedAt !== null) {
        apiEndedAt = Date.now();
      }

      const isLoading = apiLoading || tableLoading;

      if (isLoading) {
        hasStartedLoading = true;

        return;
      }

      if (!hasStartedLoading) {
        return;
      }

      const elapsedMs = Date.now() - startedAt;

      if (elapsedMs >= SLOW_TABLE_LOAD_THRESHOLD_MS) {
        reportSlowTableLoad(buildReport(), {
          elapsedMs,
          apiElapsedMs: apiElapsedMs(),
          completed: true,
        });
      }

      clearTableLoadTimer();
    },
  );
};
